import { ReactNode, useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { LayoutDashboard, BookOpen, MessageCircle, ChevronRight, ChevronDown, Stethoscope, TrendingUp, Target, Wrench, Award, CreditCard, Menu, X, Sparkles, Building2 } from 'lucide-react';
import logoLiderIA from '@/assets/logo_lider_ia_white.png';
import { AnnouncementBanner } from '@/components/public/AnnouncementBanner';
import { AnnouncementPopover } from '@/components/public/AnnouncementPopover';
import { FloatingWhatsAppButton } from '@/components/FloatingWhatsAppButton';
import { usePublicRoutesEnabled } from '@/hooks/usePublicRoutesEnabled';
import { SidebarFooterLinks } from '@/components/SidebarFooterLinks';
interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  children?: NavItem[];
  hiddenForGuest?: boolean;
  isSeparator?: boolean;
}

// Public menu - visible items for guests, some hidden until auth
const publicNav: NavItem[] = [
  {
    label: 'Inicio',
    path: '/',
    icon: <LayoutDashboard className="w-5 h-5" />
  },
  {
    label: 'Tendencias de IA',
    path: '/news',
    icon: <TrendingUp className="w-5 h-5" />
  },
  {
    label: 'Contenido',
    path: '/content',
    icon: <BookOpen className="w-5 h-5" />
  },
  {
    label: 'Comunidad',
    path: '/forum',
    icon: <MessageCircle className="w-5 h-5" />
  },
  {
    label: 'Herramientas de IA',
    path: '/ai-tools',
    icon: <Wrench className="w-5 h-5" />
  },
  {
    label: 'Diagnóstico Personal',
    path: '/diagnostic',
    icon: <Stethoscope className="w-5 h-5" />
  },
  {
    label: 'Diagnóstico Empresarial',
    path: '/diagnostico-empresarial',
    icon: <Building2 className="w-5 h-5" />
  },
  {
    label: 'Proyectos',
    path: '/challenge',
    icon: <Target className="w-5 h-5" />
  },
  // Separator
  { label: '', path: '', icon: null, isSeparator: true },
  {
    label: 'Rutas',
    path: '/rutas',
    icon: <Award className="w-5 h-5" />
  },
  {
    label: 'Planes',
    path: '/planes',
    icon: <CreditCard className="w-5 h-5" />
  },
  {
    label: 'Nuestro Impacto',
    path: '/impacto',
    icon: <Sparkles className="w-5 h-5" />
  }
];

// Items only visible for authenticated users
const authOnlyNav: NavItem[] = [{
  label: 'Mis Métricas',
  path: '/metrics',
  icon: <Target className="w-5 h-5" />,
  hiddenForGuest: true
}, {
  label: 'Calendario',
  path: '/calendar',
  icon: <Target className="w-5 h-5" />,
  hiddenForGuest: true
}, {
  label: 'Soporte',
  path: '/support',
  icon: <Target className="w-5 h-5" />,
  hiddenForGuest: true
}];
function NavItemComponent({
  item,
  onNavigate
}: {
  item: NavItem;
  onNavigate?: () => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const hasChildren = item.children && item.children.length > 0;
  const handleClick = (e: React.MouseEvent) => {
    if (hasChildren) {
      e.preventDefault();
      setIsOpen(!isOpen);
    } else if (onNavigate) {
      onNavigate();
    }
  };
  // Handle separator items
  if (item.isSeparator) {
    return <li className="my-2">
      <div className="border-t border-white/20 mx-2" />
    </li>;
  }

  return <li>
      <div className="flex flex-col">
        <NavLink to={item.path} onClick={handleClick} className={({
        isActive: linkActive
      }) => cn('flex items-center justify-between px-4 py-3 rounded-xl text-sm transition-all duration-300 group', linkActive && !hasChildren ? 'bg-primary text-primary-foreground font-semibold shadow-lg shadow-primary/25' : 'text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-foreground')}>
          <div className="flex items-center gap-3">
            <span className="transition-transform duration-300 group-hover:scale-110">{item.icon}</span>
            <span>{item.label}</span>
          </div>
          {hasChildren && <span className="text-sidebar-foreground/60">
              {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </span>}
        </NavLink>
        
        {hasChildren && isOpen && <ul className="ml-6 mt-2 space-y-1 border-l-2 border-sidebar-border pl-4">
            {item.children?.map(child => <li key={child.path}>
                <NavLink to={child.path} onClick={onNavigate} className={({
            isActive: childActive
          }) => cn('flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm transition-all duration-300', childActive ? 'bg-sidebar-primary/20 text-sidebar-primary font-medium' : 'text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-sidebar-foreground')}>
                  {child.icon}
                  <span>{child.label}</span>
                </NavLink>
              </li>)}
          </ul>}
      </div>
    </li>;
}
function SidebarContent({
  user,
  navigate,
  onNavigate,
  routesEnabled
}: {
  user: { email: string } | null;
  navigate: (path: string) => void;
  onNavigate?: () => void;
  routesEnabled: boolean;
}) {
  return <>
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-20 -right-20 w-40 h-40 bg-secondary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 -left-10 w-32 h-32 bg-accent/10 rounded-full blur-3xl" />
      </div>

      {/* Logo */}
      <div className="p-6 border-b border-sidebar-border relative z-10">
        <div className="flex items-center cursor-pointer hover:opacity-80 transition-opacity" onClick={() => {
        navigate("/");
        onNavigate?.();
      }}>
          <img alt="LÍDER IA" className="h-8 w-auto" src={logoLiderIA} />
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 overflow-y-auto relative z-10 scrollbar-custom">
        <ul className="space-y-2">
          {publicNav.filter(item => !(item.path === '/rutas' && !routesEnabled)).map(item => <NavItemComponent key={item.path} item={item} onNavigate={onNavigate} />)}
          {/* Auth-only items - only show when user is logged in */}
          {user && authOnlyNav.map(item => <NavItemComponent key={item.path} item={item} onNavigate={onNavigate} />)}
        </ul>
      </nav>

      <SidebarFooterLinks />

    </>;
}
export function PublicLayout({
  children
}: {
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  // Simulated user state for prototype (null = guest mode)
  const [user, setUser] = useState<{ email: string } | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { enabled: routesEnabled } = usePublicRoutesEnabled();

  // Close mobile menu and scroll to top on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);
  
  const closeMobileMenu = () => setMobileMenuOpen(false);
  const currentPublicPath = location.pathname + location.search;
  const authPath = (mode?: 'signup') =>
    `/auth?${mode ? 'mode=signup&' : ''}next=${encodeURIComponent(currentPublicPath)}`;
  return <div className="min-h-screen bg-background bg-pattern">
      {/* Desktop Sidebar - Fixed, hidden on mobile */}
      <aside className="hidden lg:flex w-72 gradient-dark flex-col shadow-2xl fixed top-0 left-0 h-screen z-50">
        <SidebarContent user={user} navigate={navigate} routesEnabled={routesEnabled} />
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 gradient-dark border-b border-sidebar-border flex items-center justify-between px-4 z-50">
        <div className="flex items-center cursor-pointer" onClick={() => navigate("/")}>
          <img src={logoLiderIA} alt="LÍDER IA" className="h-7 w-auto" />
        </div>
        <div className="flex items-center gap-2">
          <AnnouncementPopover className="bg-white/10 text-white hover:bg-white/20" />
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 rounded-lg text-sidebar-foreground hover:bg-sidebar-accent transition-colors">
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={closeMobileMenu} />}

      {/* Mobile Sidebar */}
      <aside className={cn("lg:hidden fixed top-16 left-0 w-72 h-[calc(100vh-4rem)] gradient-dark flex flex-col shadow-2xl z-50 transition-transform duration-300 ease-in-out", mobileMenuOpen ? "translate-x-0" : "-translate-x-full")}>
        <SidebarContent user={user} navigate={navigate} onNavigate={closeMobileMenu} routesEnabled={routesEnabled} />
      </aside>

      {/* Main content */}
      <div className="lg:ml-72 min-h-screen flex flex-col pt-16 lg:pt-0">
        {/* Desktop Header */}
        <header className="hidden lg:flex h-16 border-b border-border bg-card/80 backdrop-blur-xl items-center justify-between px-6 sticky top-0 z-40">
          <div className="flex-1 min-w-0 mr-4 flex items-center"><AnnouncementBanner /></div>
          <div className="flex items-center gap-3">
            {user ? <Button onClick={() => navigate("/dashboard")}>
                Ir al Dashboard
              </Button> : <>
                <Button variant="ghost" onClick={() => navigate(authPath())}>
                  Iniciar Sesión
                </Button>
                <Button onClick={() => navigate(authPath('signup'))}>
                  Registrarse
                </Button>
                <Button variant="outline" className="border-secondary text-secondary hover:bg-secondary hover:text-white" onClick={() => navigate("/empresas")}>
                  Para Empresas
                </Button>
              </>}
          </div>
        </header>

        {/* Mobile Bottom Action Bar */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-card/95 backdrop-blur-xl border-t border-border p-3 z-40">
          <div className="flex items-center justify-center gap-2">
            {user ? <Button className="flex-1" onClick={() => navigate("/dashboard")}>
                Ir al Dashboard
              </Button> : <>
                <Button variant="outline" className="flex-1" onClick={() => navigate(authPath())}>
                  Iniciar Sesión
                </Button>
                <Button className="flex-1" onClick={() => navigate(authPath('signup'))}>
                  Registrarse
                </Button>
                <Button variant="outline" className="flex-1 border-secondary text-secondary" onClick={() => navigate("/empresas")}>
                  Empresas
                </Button>
              </>}
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-6 overflow-auto pb-24 lg:pb-6">
          <div className="animate-fade-in max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>

      <FloatingWhatsAppButton />
    </div>;
}